import { create } from 'zustand';
import { checkOverlapForCreate, checkOverlapForUpdate, createSingleShift, updateSingleShift } from '@libs/api/shifts';
import { publishShifts, SingleShiftCreateData, SingleShiftEditData } from '@libs/api/shifts';
import { getSingleShift } from '@libs/api/shifts';
import { Shift } from '@libs/models/shifts';
import dayjs from 'dayjs';

type State = {
  loading: boolean;
  drawerOpen: boolean;
  shiftId: string;
  drawerMode: 'add' | 'edit' | 'copy';
  initiated: boolean;
  data: Shift | null;
  load: (id: string) => Promise<void>;
  openShiftDrawer: (id?: string, entity?: Shift, drawerMode?: 'add' | 'edit' | 'copy') => void;
  closeShiftDrawer: () => void;
  createShift: (shift: SingleShiftCreateData) => Promise<string[]>;
  checkCreateOverlap: (params: {
    startDate: dayjs.Dayjs;
    endDate: dayjs.Dayjs;
    timeZone: string;
    staffingType: 'All' | 'Internal' | 'External';
    isUrgent: boolean;
    departmentNodeId: string;
    subDepartmentNodeId?: string;
    locationId: string;
    qualificationIds: string[];
    specialtyId?: string;
    assigneeId: string[];
  }) => Promise<{
    canCreateDoubleShift: true;
    overlappedShiftId: string | null;
  }>;
  updateShift: (shiftId: string, shift: SingleShiftEditData) => Promise<void>;
  checkUpdateOverlap: (
    id: string,
    assigneeId: string,
  ) => Promise<{
    canCreateDoubleShift: true;
    overlappedShiftId: string | null;
  }>;
  publishShift: (shiftId: string) => Promise<void>;
  clearCurrentShift: () => void;
};

export const useShiftAddEditStore = create<State>()((set, get) => ({
  loading: false,
  drawerOpen: false,
  drawerMode: 'add',
  initiated: false,
  shiftId: '',
  data: null,
  load: async (id) => {
    set({ loading: true });
    try {
      const shift = await getSingleShift(id);
      set({
        loading: false,
        data: shift,
      });
    } finally {
      set({ loading: false, initiated: true });
    }
  },
  openShiftDrawer: (id = undefined, entity = undefined, mode = undefined) => {
    const drawerMode = mode || (id ? 'edit' : 'add');
    set({ drawerMode, shiftId: id, data: entity || null, drawerOpen: true });
  },
  closeShiftDrawer: () => {
    set({ drawerOpen: false, shiftId: '' });
  },
  updateShift: async (shiftId, shiftData) => {
    await updateSingleShift(shiftId, shiftData);
  },
  checkUpdateOverlap: async (id, assigneeId) => {
    const response = await checkOverlapForUpdate({ shiftID: id, assigneeId });
    return response;
  },
  createShift: async (shift) => {
    set({ loading: true });
    const shiftIds = await createSingleShift({
      ...shift,
    }).finally(() => {
      set({ loading: false });
    });

    return shiftIds;
  },
  checkCreateOverlap: async (params) => {
    const response = await checkOverlapForCreate(params);
    return response;
  },
  publishShift: async (shiftId: string) => {
    await publishShifts([shiftId]);
  },
  clearCurrentShift: () => {
    set({ data: null });
  },
}));
